// Generische Drop-Routing-Logik: wenn ein Objekt in einer akzeptierenden Region
// gedroppt wird, rastet es einen freien Slot + wird Region-Member (parentRegionId).
// Traegt die Region eine semanticHint, werden ALLE Hint-Keys aufs Objekt gepatcht;
// hint-lose Regionen (Lanes, Phasen, Context-Regionen) liefern einen leeren Patch.
//
// Caller MUSS innerhalb yMap.doc.transact aufrufen (Schreib-Op auf yMap).

import {
  type CanvasObject,
  type FrameObj,
  REGION_PADDING,
  type RegionObj,
  isCanvasObject,
  methodPlacesFreely,
} from "@ms-whiteboard/shared";
import type * as Y from "yjs";
import { objectBounds } from "./object-bounds.js";
import { findFreeSlotWithAutoExpand } from "./region-routing.js";

const FRAME_PAD = 8;

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(v, hi));
}

/** Frei-Position-Familien (process/diagram) behalten die Drop-Position; cluster/matrix snappen. */
function regionPlacesFreely(yMap: Y.Map<unknown>, region: RegionObj): boolean {
  const block = yMap.get(region.blockId);
  if (!isCanvasObject(block) || block.type !== "block") return false;
  return methodPlacesFreely(block.methodId);
}

export interface AutoRouteResult {
  x: number;
  y: number;
  /** Schluessel/Wert-Patch fuer die hint-getriebenen Properties. Leer bei hint-loser Region. */
  hintPatch: Record<string, string>;
}

export function autoRouteIntoRegion(
  yMap: Y.Map<unknown>,
  obj: CanvasObject,
  region: RegionObj,
): AutoRouteResult | null {
  if (!regionAcceptsType(region, obj.type)) return null;
  const bounds = objectBounds(obj);
  const hintPatch = { ...(region.semanticHint ?? {}) };

  if (regionPlacesFreely(yMap, region)) {
    // Frei: Drop-Position behalten, nur in die Region-Innenbounds clampen (kein Grid-Snap).
    const x0 = region.x + REGION_PADDING.outer;
    const y0 = region.y + REGION_PADDING.header;
    const x1 = region.x + region.width - REGION_PADDING.outer - bounds.w;
    const y1 = region.y + region.height - REGION_PADDING.outer - bounds.h;
    return {
      x: clamp(obj.x, x0, Math.max(x0, x1)),
      y: clamp(obj.y, y0, Math.max(y0, y1)),
      hintPatch,
    };
  }

  const slot = findFreeSlotWithAutoExpand(
    yMap,
    region,
    { width: bounds.w, height: bounds.h },
    obj.id,
  );
  return { x: slot.x, y: slot.y, hintPatch };
}

/** Frame-Membership: clamped Drop-Position in die Frame-Bounds (immer frei, kein Grid). */
export function autoRouteIntoFrame(frame: FrameObj, obj: CanvasObject): { x: number; y: number } {
  const bounds = objectBounds(obj);
  const x0 = frame.x + FRAME_PAD;
  const y0 = frame.y + FRAME_PAD;
  const x1 = frame.x + frame.width - FRAME_PAD - bounds.w;
  const y1 = frame.y + frame.height - FRAME_PAD - bounds.h;
  return {
    x: clamp(obj.x, x0, Math.max(x0, x1)),
    y: clamp(obj.y, y0, Math.max(y0, y1)),
  };
}

/** Pruefe ob die Region den Objekt-Typ akzeptiert (accepts=any oder accepts=type). */
export function regionAcceptsType(region: RegionObj, type: string): boolean {
  return region.accepts === "any" || region.accepts === type;
}
